import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ChurchBrandMark } from '@/components/church-brand-mark';
import { api, ApiError } from '@/lib/api';

interface ChurchSearchItem {
  id: string;
  name: string;
  campusName: string;
  slug: string;
  logoUrl: string | null;
  locationLabel?: string | null;
  distanceMetres?: number;
}

interface GivingLinkSummary {
  slug: string;
  title: string;
  description: string | null;
}

interface ChurchGivingResponse {
  link: { slug: string; title: string };
  links: GivingLinkSummary[];
}

interface ChurchSearchCollection {
  data: ChurchSearchItem[];
}

interface LinkPickerState {
  church: ChurchSearchItem;
  links: GivingLinkSummary[];
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const campusSubtitle = (church: ChurchSearchItem): string | null => {
  const parts: string[] = [];
  const campus = church.campusName?.trim();
  if (campus && campus.toLocaleLowerCase() !== church.name.toLocaleLowerCase()) {
    parts.push(campus);
  }
  const location = church.locationLabel?.trim();
  if (location) parts.push(location);
  if (church.distanceMetres != null) {
    const km = church.distanceMetres / 1000;
    parts.push(km < 1 ? `${church.distanceMetres} m away` : `${km.toFixed(1)} km away`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

const ChurchAvatar = ({ church }: { church: Pick<ChurchSearchItem, 'logoUrl' | 'name'> }) =>
  church.logoUrl ? (
    <img src={church.logoUrl} alt="" className="church-brand-logo" />
  ) : (
    <div className="church-brand-logo-fallback" aria-hidden>
      <svg viewBox="0 0 24 24" aria-hidden>
        <path d="M12 3 2 9v2h2v8h5v-6h6v6h5v-8h2V9L12 3zm0 2.8 6 4v1.2h-1v6h-3v-6H10v6H7v-6H6V9.8l6-4z" />
      </svg>
    </div>
  );

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" className="give-list-button-chevron" aria-hidden>
    <path
      fill="currentColor"
      d="M9.29 6.71a.996.996 0 0 0 0 1.41L13.17 12l-3.88 3.88a.996.996 0 1 0 1.41 1.41l4.59-4.59a.996.996 0 0 0 0-1.41L10.7 6.7a.996.996 0 0 0-1.41.01z"
    />
  </svg>
);

export const ChurchSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchInputId = useId();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<ChurchSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [linkPicker, setLinkPicker] = useState<LinkPickerState | null>(null);
  const [noGivingChurch, setNoGivingChurch] = useState<ChurchSearchItem | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearching(false);
      setSearchError(false);
      return;
    }

    let cancelled = false;

    const runSearch = async () => {
      setSearching(true);
      setSearchError(false);

      try {
        const response = await api.get<ChurchSearchCollection>('/gateway/churches/search', {
          q: debouncedQuery,
          page: 1,
          pageSize: 20,
        });
        if (!cancelled) {
          setResults(response.data);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
          setSearchError(true);
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    };

    void runSearch();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const openGivingLink = useCallback(
    (church: ChurchSearchItem, linkSlug: string) => {
      navigate(`/${encodeURIComponent(church.slug)}/${encodeURIComponent(linkSlug)}`);
    },
    [navigate],
  );

  const openChurch = useCallback(
    async (church: ChurchSearchItem) => {
      if (openingId) return;

      setOpeningId(church.id);
      setNoGivingChurch(null);
      setLinkPicker(null);

      try {
        const data = await api.get<ChurchGivingResponse>(`/gateway/churches/${church.id}/giving`);
        const links =
          data.links.length > 0
            ? data.links
            : [{ slug: data.link.slug, title: data.link.title, description: null }];

        if (links.length === 1) {
          openGivingLink(church, links[0].slug);
          return;
        }

        setLinkPicker({ church, links });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'GIVING_NOT_AVAILABLE') {
          setNoGivingChurch(church);
          return;
        }
        setSearchError(true);
      } finally {
        setOpeningId(null);
      }
    },
    [openGivingLink, openingId],
  );

  const trimmedQuery = query.trim();
  const showHint = trimmedQuery.length === 0;
  const showMinLength = trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH;
  const showEmpty =
    debouncedQuery.length >= MIN_QUERY_LENGTH && !searching && !searchError && results.length === 0;
  const showResults = debouncedQuery.length >= MIN_QUERY_LENGTH && results.length > 0;

  if (linkPicker) {
    const { church, links } = linkPicker;

    return (
      <section className="stack">
        <ChurchBrandMark
          churchName={church.name}
          campusName={church.campusName}
          logoUrl={church.logoUrl}
          compact
        />

        <div className="stack-tight">
          <p className="field-label">{t('home.chooseLink')}</p>
          <ul className="give-list">
            {links.map((link) => (
              <li key={link.slug} className="give-list-item">
                <button
                  type="button"
                  className="give-list-button give-list-button--stacked"
                  onClick={() => openGivingLink(church, link.slug)}
                >
                  <span className="give-list-button-title">{link.title}</span>
                  {link.description ? (
                    <span className="give-list-button-subtitle">{link.description}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="give-actions">
          <button type="button" className="linklike" onClick={() => setLinkPicker(null)}>
            {t('home.backToSearch')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="stack">
      <div className="give-field">
        <label className="field-label" htmlFor={searchInputId}>
          {t('home.searchLabel')}
        </label>
        <input
          id={searchInputId}
          type="search"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('home.searchPlaceholder')}
          className="give-control"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {searching ? (
        <p className="muted" role="status">
          {t('home.searching')}
        </p>
      ) : null}

      {showHint ? <p className="field-hint">{t('home.searchHint')}</p> : null}

      {showMinLength ? <p className="muted">{t('home.searchMinLength')}</p> : null}

      {searchError ? (
        <p className="give-message give-message--error" role="alert">
          {t('states.error')}
        </p>
      ) : null}

      {noGivingChurch ? (
        <div className="give-notice">
          <p className="give-notice-title">{noGivingChurch.name}</p>
          <p className="give-notice-body">{t('home.noGiving')}</p>
        </div>
      ) : null}

      {showEmpty ? <p className="muted">{t('home.noResults')}</p> : null}

      {showResults ? (
        <ul className="give-list">
          {results.map((church) => {
            const isOpening = openingId === church.id;
            const subtitle = campusSubtitle(church);

            return (
              <li key={church.id} className="give-list-item">
                <button
                  type="button"
                  disabled={openingId !== null}
                  className="give-list-button"
                  onClick={() => void openChurch(church)}
                >
                  <ChurchAvatar church={church} />
                  <span className="give-list-button-text">
                    <span className="give-list-button-title">{church.name}</span>
                    {subtitle ? (
                      <span className="give-list-button-subtitle">{subtitle}</span>
                    ) : null}
                  </span>
                  {isOpening ? (
                    <span className="give-list-button-meta">{t('home.opening')}</span>
                  ) : (
                    <ChevronRight />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
};
